"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Card,
  CardBody,
  CardHeader,
  useToast,
  Spinner,
  Flex,
} from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import { campaignService, Campaign } from "../voice-services/campaignService";
import { smsAgentService, SMSAgent } from "../sms-services/agentService";
import { emailAgentService, EmailAgent } from "../email-services/agentService";
import { useAuth } from "../contexts/AuthContext";

const CampaignConfig: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { credits, creditsLoading } = useAuth(); // Get creditsLoading state

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [voiceAgentsWithPhones, setVoiceAgentsWithPhones] = useState<{
    agents: any[];
    agentPhoneMap: Record<string, string[]>;
  }>({ agents: [], agentPhoneMap: {} });
  const [smsAgents, setSmsAgents] = useState<SMSAgent[]>([]);
  const [emailAgents, setEmailAgents] = useState<EmailAgent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const hasMountedRef = useRef(false);
  const hasShownCreditWarning = useRef(false);

  useEffect(() => {
    // Only check credits once on initial mount after loading is complete
    if (
      !hasMountedRef.current &&
      !creditsLoading &&
      credits !== null &&
      credits < 0.01 &&
      !hasShownCreditWarning.current
    ) {
      hasShownCreditWarning.current = true;
      toast({
        title: "Insufficient Credits",
        description:
          "You need to have credits in your wallet in order to access campaigns.",
        status: "error",
        duration: 5000,
      });
      navigate("/campaigns");
    }

    // Mark as mounted after first render
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
    }
  }, [credits, creditsLoading, navigate, toast]); // Add creditsLoading to dependencies

  useEffect(() => {
    const loadData = async () => {
      try {
        const [campaignData, voiceData, smsData, emailData] =
          await Promise.all([
            campaignService.get(campaignId!),
            campaignService.getVoiceAgentsWithPhones(),
            campaignService.getSMSAgentsWithPhones(),
            campaignService.getEmailAgentsWithAddresses(),
          ]);

        setCampaign(campaignData);
        setVoiceAgentsWithPhones(voiceData);
        setSmsAgents(smsData);
        setEmailAgents(emailData);
      } catch (error) {
        toast({ title: "Error loading data", status: "error", duration: 3000 });
        navigate("/campaigns");
      } finally {
        setIsLoading(false);
      }
    };

    if (campaignId) {
      loadData();
    }
  }, [campaignId, navigate, toast]);

  const handleSave = async () => {
    if (!campaign || !campaignId) return;

    setIsSaving(true);
    try {
      await campaignService.update(campaignId, campaign);
      toast({
        title: "Campaign updated successfully",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Error updating campaign",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const updateCampaign = (field: keyof Campaign, value: any) => {
    if (campaign) {
      setCampaign({ ...campaign, [field]: value });
    }
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100vh">
        <Spinner size="xl" color="primary.500" />
      </Flex>
    );
  }

  if (!campaign) {
    return (
      <Box p={6}>
        <Text>Campaign not found</Text>
      </Box>
    );
  }

  return (
    <Box p={6} bg="white" minH="100vh">
      <Flex justify="space-between" align="center" mb={6}>
        <HStack>
          <Button
            variant="ghost"
            color="black"
            _hover={{ bg: "gray.100" }}
            onClick={() => navigate("/campaigns")}
          >
            ← Back
          </Button>
          <Text fontSize="2xl" fontWeight="bold" color="black">
            Configure Campaign
          </Text>
        </HStack>
        <HStack>
          <Button
            variant="ghost"
            color="black"
            _hover={{ bg: "gray.100" }}
            onClick={() => navigate("/campaigns")}
          >
            Cancel
          </Button>
          <Button
            colorScheme="primary"
            bg="primary.800"
            _hover={{ bg: "primary.900" }}
            onClick={handleSave}
            isLoading={isSaving}
          >
            Save Changes
          </Button>
        </HStack>
      </Flex>

      <VStack spacing={6} align="stretch" maxW="800px">
        <Card>
          <CardHeader>
            <Text fontSize="lg" fontWeight="bold">
              Basic Settings
            </Text>
          </CardHeader>
          <CardBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Campaign Name</FormLabel>
                <Input
                  value={campaign.name}
                  onChange={(e) => updateCampaign("name", e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={campaign.status}
                  onChange={(e) => updateCampaign("status", e.target.value)}
                >
                  <option value="draft">Draft</option>
                  <option value="running">Running</option>
                  <option value="paused">Paused</option>
                  <option value="completed">Completed</option>
                </Select>
              </FormControl>
            </VStack>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <Text fontSize="lg" fontWeight="bold">
              Agent Configuration
            </Text>
          </CardHeader>
          <CardBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Voice Agent</FormLabel>
                <Select
                  value={campaign.voice_agent_id || ""}
                  onChange={(e) =>
                    updateCampaign("voice_agent_id", e.target.value || null)
                  }
                >
                  <option value="">Select Voice Agent</option>
                  {voiceAgentsWithPhones.agents.map((agent) => (
                    <option key={agent.agent_id} value={agent.agent_id}>
                      {agent.agent_name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>SMS Agent</FormLabel>
                <Select
                  value={campaign.sms_agent_id || ""}
                  onChange={(e) =>
                    updateCampaign("sms_agent_id", e.target.value || null)
                  }
                >
                  <option value="">Select SMS Agent</option>
                  {smsAgents.map((agent) => (
                    <option key={agent.agent_id} value={agent.agent_id}>
                      {agent.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              {process.env.REACT_APP_IS_MAIN_APP === "true" && (
                <FormControl>
                  <FormLabel>Email Agent</FormLabel>
                  <Select
                    value={campaign.email_agent_id || ""}
                    onChange={(e) =>
                      updateCampaign("email_agent_id", e.target.value || null)
                    }
                  >
                    <option value="">Select Email Agent</option>
                    {emailAgents.map((agent) => (
                      <option key={agent.agent_id} value={agent.agent_id}>
                        {agent.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl>
                <FormLabel>WhatsApp Agent</FormLabel>
                <Select
                  value={campaign.whatsapp_agent_id || ""}
                  onChange={(e) =>
                    updateCampaign("whatsapp_agent_id", e.target.value || null)
                  }
                >
                  <option value="">Select WhatsApp Agent (Coming Soon)</option>
                </Select>
              </FormControl>
            </VStack>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <Text fontSize="lg" fontWeight="bold">
              Step Delays
            </Text>
            <Text fontSize="sm" color="gray.600" mt={1}>
              Time to wait before moving to next channel (in seconds)
            </Text>
          </CardHeader>
          <CardBody>
            <VStack spacing={4}>
              {campaign.voice_agent_id && (
                <FormControl>
                  <FormLabel>Voice Delay (seconds)</FormLabel>
                  <Input
                    type="number"
                    min="0"
                    value={campaign.sequence_config?.delays?.voice ?? ""}
                    placeholder="300"
                    onChange={(e) => {
                      const value =
                        e.target.value === ""
                          ? undefined
                          : parseInt(e.target.value);
                      const delays = {
                        ...campaign.sequence_config?.delays,
                        voice: value,
                      };
                      updateCampaign("sequence_config", {
                        ...campaign.sequence_config,
                        delays,
                      });
                    }}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {Math.floor(
                      (campaign.sequence_config?.delays?.voice || 300) / 60
                    )}{" "}
                    minutes
                  </Text>
                </FormControl>
              )}
              {campaign.sms_agent_id && (
                <FormControl>
                  <FormLabel>SMS Delay (seconds)</FormLabel>
                  <Input
                    type="number"
                    min="0"
                    value={campaign.sequence_config?.delays?.sms ?? ""}
                    placeholder="300"
                    onChange={(e) => {
                      const value =
                        e.target.value === ""
                          ? undefined
                          : parseInt(e.target.value);
                      const delays = {
                        ...campaign.sequence_config?.delays,
                        sms: value,
                      };
                      updateCampaign("sequence_config", {
                        ...campaign.sequence_config,
                        delays,
                      });
                    }}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {Math.floor(
                      (campaign.sequence_config?.delays?.sms || 300) / 60
                    )}{" "}
                    minutes
                  </Text>
                </FormControl>
              )}
              {campaign.email_agent_id && (
                <FormControl>
                  <FormLabel>Email Delay (seconds)</FormLabel>
                  <Input
                    type="number"
                    min="0"
                    value={campaign.sequence_config?.delays?.email ?? ""}
                    placeholder="300"
                    onChange={(e) => {
                      const value =
                        e.target.value === ""
                          ? undefined
                          : parseInt(e.target.value);
                      const delays = {
                        ...campaign.sequence_config?.delays,
                        email: value,
                      };
                      updateCampaign("sequence_config", {
                        ...campaign.sequence_config,
                        delays,
                      });
                    }}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {Math.floor(
                      (campaign.sequence_config?.delays?.email || 300) / 60
                    )}{" "}
                    minutes
                  </Text>
                </FormControl>
              )}
              {campaign.whatsapp_agent_id && (
                <FormControl>
                  <FormLabel>WhatsApp Delay (seconds)</FormLabel>
                  <Input
                    type="number"
                    min="0"
                    value={campaign.sequence_config?.delays?.whatsapp ?? ""}
                    placeholder="300"
                    onChange={(e) => {
                      const value =
                        e.target.value === ""
                          ? undefined
                          : parseInt(e.target.value);
                      const delays = {
                        ...campaign.sequence_config?.delays,
                        whatsapp: value,
                      };
                      updateCampaign("sequence_config", {
                        ...campaign.sequence_config,
                        delays,
                      });
                    }}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {Math.floor(
                      (campaign.sequence_config?.delays?.whatsapp || 300) / 60
                    )}{" "}
                    minutes
                  </Text>
                </FormControl>
              )}
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default CampaignConfig;
